import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Search, Refresh, GetApp, ArrowBack, Close } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar, InputAdornment, TextField, Modal, CircularProgress } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const fmtQty = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PRIMARY = '#4a148c';
const PRIMARY_MID = '#8e24aa';
const PRIMARY_LT = '#f3e5f5';
const th  = { padding: '8px 10px', textAlign: 'left',   fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)' };
const td  = { padding: '7px 10px', textAlign: 'left',   fontSize: 12, whiteSpace: 'nowrap' };
const tdR = { padding: '7px 10px', textAlign: 'right',  fontSize: 12, whiteSpace: 'nowrap' };
const tdC = { padding: '7px 10px', textAlign: 'center', fontSize: 12, whiteSpace: 'nowrap' };

const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const AgeBadge = ({ value, bg, color }) => {
  const qty = parseFloat(value) || 0;
  if (qty === 0) return <span style={{ color: '#94a3b8', fontSize: 11 }}>-</span>;
  return (
    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: bg, color }}>
      {fmtQty(qty)}
    </span>
  );
};

const ageColor = (avgDays) => {
  const d = parseFloat(avgDays) || 0;
  if (d <= 30)  return { bg: '#d1fae5', color: '#059669' };
  if (d <= 60)  return { bg: '#fef3c7', color: '#d97706' };
  if (d <= 90)  return { bg: '#fed7aa', color: '#ea580c' };
  return              { bg: '#fecaca', color: '#dc2626' };
};

const StockAgeingReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const [data, setData]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [fetched, setFetched]       = useState(false);
  const [search, setSearch]         = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  // Batch-wise modal
  const [modal, setModal]           = useState({ open: false, item: null });
  const [batchData, setBatchData]   = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);

  const openBatchModal = async (row) => {
    setModal({ open: true, item: row });
    setBatchData([]);
    setBatchLoading(true);
    try {
      const res = await fetchData('StockReportApi', `/StockAgeingBatchWise/${row.ItemId}/${companyId}/${branchId}`, token);
      const rows = res.response_data || res || [];
      setBatchData(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error(err);
    } finally {
      setBatchLoading(false);
    }
  };

  const closeModal = () => { setModal({ open: false, item: null }); setBatchData([]); };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await fetchData('StockReportApi', `/StockAgeing/${companyId}/${branchId}`, token);
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch stock ageing report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]); // eslint-disable-line

  const filtered = useMemo(() => {
    // Fill blank ItemName with previous item's name
    let lastItemName = '';
    const filled = data.map(r => {
      if (r.ItemName && r.ItemName.trim() !== '') {
        lastItemName = r.ItemName.trim();
        return { ...r, ItemName: r.ItemName.trim(), _isItem: true };
      }
      return { ...r, ItemName: lastItemName, _isItem: false };
    });
    if (!search.trim()) return filled;
    const q = search.toLowerCase();
    return filled.filter(r =>
      (r.ItemName    || '').toLowerCase().includes(q) ||
      (r.BatchNumber || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  // Totals
  const totalQty    = filtered.reduce((s, r) => s + (parseFloat(r.TotalQty)    || 0), 0);
  const total0_30   = filtered.reduce((s, r) => s + (parseFloat(r.Days_0_30)   || 0), 0);
  const total31_60  = filtered.reduce((s, r) => s + (parseFloat(r.Days_31_60)  || 0), 0);
  const total61_90  = filtered.reduce((s, r) => s + (parseFloat(r.Days_61_90)  || 0), 0);
  const total90Plus = filtered.reduce((s, r) => s + (parseFloat(r.Days_90_Plus)|| 0), 0);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'StockAgeing', 0);
    if (!filtered.length) return;
    const cols = ['#', 'Item Name', 'Stock In Date', 'Total Qty', '0-30 Days', '31-60 Days', '61-90 Days', '90+ Days'];
    const rows = filtered.map((r, i) => [
      i + 1, r.ItemName || '', r.StockInDate || '',
      // '', r.BatchNumber ||
      parseFloat(r.TotalQty) || 0,
      parseFloat(r.Days_0_30) || 0, parseFloat(r.Days_31_60) || 0,
      parseFloat(r.Days_61_90) || 0, parseFloat(r.Days_90_Plus) || 0,
      //(parseFloat(r.AvgAgeDays) || 0).toFixed(1),
    ]);

      await exportStyledExcel({
          reportTitle: 'STOCK AGEING REPORT',
          sheetName: 'Stock Ageing',
          fileName: `StockAgeingReport.xlsx`,
          companyName,
          username,
          headers: cols,
          rows,
          totalsRow: ['', '', 'Totals', totalQty, total0_30, total31_60, total61_90, total90Plus],
          columnWidths: [4,28,14,14,12,12,12,12,12,12],
      });
  };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading stock ageing report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #6a1b9a 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Stock Ageing Report</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          {searchOpen ? (
            <>
              <TextField autoFocus size="small" placeholder="Search item, batch..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)' }} /></InputAdornment>,
                  endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')} sx={{ color: 'rgba(255,255,255,0.6)', p: '2px' }}><Close sx={{ fontSize: 14 }} /></IconButton></InputAdornment> : null,
                }}
                sx={{ flexShrink: 0, width: 200, '& .MuiOutlinedInput-root': { height: 29, fontSize: 12, borderRadius: '8px', color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.7)' } }, '& input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 } }}
              />
              <Tooltip title="Close" arrow>
                <IconButton size="small" onClick={() => { setSearchOpen(false); setSearch(''); }} sx={iconBtn}>
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Search" arrow>
              <IconButton size="small" onClick={() => setSearchOpen(true)} sx={iconBtn}>
                <Search sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 12, padding: '6px 14px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
          {[
            { label: '0-30 Days', bg: '#d1fae5', color: '#059669' },
            { label: '31-60 Days', bg: '#fef3c7', color: '#d97706' },
            { label: '61-90 Days', bg: '#fed7aa', color: '#ea580c' },
            { label: '90+ Days', bg: '#fecaca', color: '#dc2626' },
          ].map(l => (
            <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: l.bg, border: `1px solid ${l.color}` }} />
              <span style={{ fontSize: 11, color: l.color, fontWeight: 600 }}>{l.label}</span>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 155px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 800 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: PRIMARY_MID, color: 'white' }}>
                <th style={th}>#</th>
                <th style={th}>Item Name</th>
                {/* <th style={th}>Batch</th> */}
                <th style={th}>Stock In Date</th>
                <th style={{ ...th, textAlign: 'right' }}>Total Qty</th>
                <th style={{ ...th, textAlign: 'center', background: '#059669' }}>0-30 Days</th>
                <th style={{ ...th, textAlign: 'center', background: '#d97706' }}>31-60 Days</th>
                <th style={{ ...th, textAlign: 'center', background: '#ea580c' }}>61-90 Days</th>
                <th style={{ ...th, textAlign: 'center', background: '#dc2626' }}>90+ Days</th>
                {/* <th style={{ ...th, textAlign: 'center' }}>Avg Age</th> */}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
                    {fetched ? 'No records found' : 'Click refresh to load report'}
                  </td>
                </tr>
              ) : (() => {
                  let itemNo = 0;
                  return filtered.map((r, i) => {
                  const ac = ageColor(r.AvgAgeDays);
                  const isItemRow = r._isItem;
                  if (isItemRow) itemNo++;
                  return (
                    <tr key={i} style={{
                      background: isItemRow ? (i % 2 === 0 ? '#fff' : PRIMARY_LT) : '#dde3f8',
                      borderBottom: '1px solid #f1f5f9',
                      borderLeft: !isItemRow ? `3px solid ${PRIMARY}` : 'none',
                      cursor: isItemRow ? 'pointer' : 'default',
                    }}
                    onClick={() => isItemRow && openBatchModal(r)}
                    >
                      <td style={td}>{isItemRow ? itemNo : ''}</td>
                      <td style={{ ...td, fontWeight: isItemRow ? 600 : 400, color: isItemRow ? '#1e293b' : '#94a3b8', paddingLeft: isItemRow ? 10 : 24 }}>
                        {r.ItemName}
                      </td>
                      {/* <td style={{ ...td, fontSize: 11 }}>
                        {r.BatchNumber
                          ? <span style={{ padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 600, background: PRIMARY_LT, color: PRIMARY }}>{r.BatchNumber}</span>
                          : <span style={{ color: '#94a3b8' }}>-</span>}
                      </td> */}
                      <td style={{ ...td, color: '#a1092fff', fontSize: 11 }}>{r.StockInDate || '-'}</td>
                      <td style={{ ...tdR, fontWeight: isItemRow ? 700 : 400, color: PRIMARY }}>{fmtQty(r.TotalQty)}</td>
                      <td style={tdC}><AgeBadge value={r.Days_0_30}    bg="#d1fae5" color="#059669" /></td>
                      <td style={tdC}><AgeBadge value={r.Days_31_60}   bg="#fef3c7" color="#d97706" /></td>
                      <td style={tdC}><AgeBadge value={r.Days_61_90}   bg="#fed7aa" color="#ea580c" /></td>
                      <td style={tdC}><AgeBadge value={r.Days_90_Plus} bg="#fecaca" color="#dc2626" /></td>
                      {/* <td style={tdC}>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: ac.bg, color: ac.color }}>
                          {(parseFloat(r.AvgAgeDays) || 0)} days
                        </span>
                      </td> */}
                    </tr>
                  );
                });
              })()}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: PRIMARY_LT, color: PRIMARY, fontWeight: 700 }}>
                  <td colSpan={3} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Totals</td>
                  <td style={tdR}>{fmtQty(totalQty)}</td>
                  <td style={tdC}>{fmtQty(total0_30)}</td>
                  <td style={tdC}>{fmtQty(total31_60)}</td>
                  <td style={tdC}>{fmtQty(total61_90)}</td>
                  <td style={tdC}>{fmtQty(total90Plus)}</td>
                  {/* <td style={td}></td> */}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Batch-wise Ageing Modal */}
      <Modal open={modal.open} onClose={closeModal}>
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          background: '#fff', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          width: '90%', maxWidth: 780, maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          fontFamily: "'Inter', sans-serif", overflow: 'hidden',
        }}>
          {/* Modal Header */}
          <div style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #6a1b9a 100%)`, gap: 8 }}>
            <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Batch-wise Ageing — {modal.item?.ItemName || ''}
            </span>
            <IconButton size="small" onClick={closeModal} sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 28, width: 28 }}>
              <Close sx={{ fontSize: 15 }} />
            </IconButton>
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', gap: 12, padding: '6px 14px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
            {[
              { label: '0-30 Days', bg: '#d1fae5', color: '#059669' },
              { label: '31-60 Days', bg: '#fef3c7', color: '#d97706' },
              { label: '61-90 Days', bg: '#fed7aa', color: '#ea580c' },
              { label: '90+ Days',  bg: '#fecaca', color: '#dc2626' },
            ].map(l => (
              <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: l.bg, border: `1px solid ${l.color}` }} />
                <span style={{ fontSize: 11, color: l.color, fontWeight: 600 }}>{l.label}</span>
              </div>
            ))}
          </div>

          {/* Modal Body */}
          <div style={{ overflow: 'auto', flex: 1 }}>
            {batchLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 }}>
                <CircularProgress size={28} sx={{ color: PRIMARY }} />
              </div>
            ) : batchData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>No batch data found</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                  <tr style={{ background: PRIMARY_MID, color: '#fff' }}>
                    <th style={th}>#</th>
                    <th style={th}>Batch No</th>
                    <th style={th}>Batch Date</th>
                    <th style={{ ...th, textAlign: 'right' }}>Batch Qty</th>
                    <th style={{ ...th, textAlign: 'center', background: '#059669' }}>0-30 Days</th>
                    <th style={{ ...th, textAlign: 'center', background: '#d97706' }}>31-60 Days</th>
                    <th style={{ ...th, textAlign: 'center', background: '#ea580c' }}>61-90 Days</th>
                    <th style={{ ...th, textAlign: 'center', background: '#dc2626' }}>90+ Days</th>
                    <th style={{ ...th, textAlign: 'right' }}>Avg Age</th>
                  </tr>
                </thead>
                <tbody>
                  {batchData.map((b, i) => {
                    const ac = ageColor(b.AvgAgeDays);
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #f1f5f9' }}>
                        <td style={td}>{i + 1}</td>
                        <td style={{ ...td, fontWeight: 600 }}>
                          <span style={{ padding: '1px 7px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: PRIMARY_LT, color: PRIMARY }}>{b.BatchNumber || b.BatchNo || b.batchNumber || b.batchNo || '-'}</span>
                        </td>
                        <td style={{ ...td, color: '#64748b', fontSize: 11 }}>{b.BatchDate || '-'}</td>
                        <td style={{ ...tdR, fontWeight: 700, color: PRIMARY }}>{fmtQty(b.BatchQty)}</td>
                        <td style={tdC}><AgeBadge value={b.Days_0_30}    bg="#d1fae5" color="#059669" /></td>
                        <td style={tdC}><AgeBadge value={b.Days_31_60}   bg="#fef3c7" color="#d97706" /></td>
                        <td style={tdC}><AgeBadge value={b.Days_61_90}   bg="#fed7aa" color="#ea580c" /></td>
                        <td style={tdC}><AgeBadge value={b.Days_90_Plus} bg="#fecaca" color="#dc2626" /></td>
                        <td style={{ ...tdR }}>
                          <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: ac.bg, color: ac.color }}>
                            {(parseFloat(b.AvgAgeDays) || 0).toFixed(1)} days
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: PRIMARY_LT, color: PRIMARY, fontWeight: 700 }}>
                    <td colSpan={3} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Totals</td>
                    <td style={tdR}>{fmtQty(batchData.reduce((s, b) => s + (parseFloat(b.BatchQty)     || 0), 0))}</td>
                    <td style={tdC}>{fmtQty(batchData.reduce((s, b) => s + (parseFloat(b.Days_0_30)    || 0), 0))}</td>
                    <td style={tdC}>{fmtQty(batchData.reduce((s, b) => s + (parseFloat(b.Days_31_60)   || 0), 0))}</td>
                    <td style={tdC}>{fmtQty(batchData.reduce((s, b) => s + (parseFloat(b.Days_61_90)   || 0), 0))}</td>
                    <td style={tdC}>{fmtQty(batchData.reduce((s, b) => s + (parseFloat(b.Days_90_Plus) || 0), 0))}</td>
                    <td style={td}></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StockAgeingReport;




